import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useSearchParams } from "react-router-dom";
import TitleCard from "@/components/feature/TitleCard";
import { filterTitles, sortTitles, sortOptions, type SortKey } from "@/hooks/useCatalog";
import type { CategoryInfo } from "@/hooks/useCatalog";

const PAGE_SIZE = 8;

interface CategoryListBoardProps {
  config: CategoryInfo;
}

export default function CategoryListBoard({ config }: CategoryListBoardProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const genre = searchParams.get("genre") || "全部";
  const sort = (searchParams.get("sort") as SortKey) || "heat";

  useEffect(() => {
    setLoading(true);
    const timer = window.setTimeout(() => setLoading(false), 260);
    return () => window.clearTimeout(timer);
  }, [genre, sort]);

  useEffect(() => {
    setPage(1);
  }, [genre, sort]);

  const list = useMemo(() => {
    return sortTitles(filterTitles({ type: config.type, genre }), sort);
  }, [config.type, genre, sort]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const visible = list.slice(0, page * PAGE_SIZE);

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (!value || value === "全部" || (key === "sort" && value === "heat")) {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next);
  };

  return (
    <section className="w-full px-4 md:px-8 py-8 md:py-12">
      <div className="mx-auto max-w-[1440px]">
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-12">
          <div className="lg:col-span-8 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6">
            <div className="flex items-center justify-between gap-3">
              <h2 className="flex items-center gap-2 text-sm font-medium text-foreground-900">
                <i className="ri-price-tag-3-line text-primary-600" aria-hidden="true"></i>
                子分类
              </h2>
              <span className="text-xs text-foreground-500">共 {list.length} 部</span>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {config.genres.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => updateParam("genre", item)}
                  className={`rounded-full px-3.5 py-1.5 text-xs whitespace-nowrap transition-colors cursor-pointer ${
                    genre === item
                      ? "bg-primary-800 text-background-50"
                      : "bg-background-100 text-foreground-700 hover:bg-secondary-100 hover:text-secondary-900"
                  }`}
                >
                  {item}
                </button>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t border-background-200 pt-4">
              <div className="flex items-center gap-1 rounded-full bg-background-100 px-1 py-1">
                {sortOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => updateParam("sort", option.key)}
                    className={`rounded-full px-3.5 py-1.5 text-xs whitespace-nowrap transition-colors cursor-pointer ${
                      sort === option.key
                        ? "bg-secondary-500 text-primary-950 font-medium"
                        : "text-foreground-600 hover:text-foreground-900"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
              <Link
                to={`/category/${config.type}/list${genre !== "全部" ? `?genre=${encodeURIComponent(genre)}` : ""}`}
                className="inline-flex items-center gap-1.5 text-xs text-primary-700 whitespace-nowrap transition-colors cursor-pointer hover:text-primary-800"
              >
                <i className="ri-filter-3-line" aria-hidden="true"></i>
                进入高级筛选
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4 rounded-lg border border-background-200 bg-background-100 p-4 md:p-6">
            <h2 className="flex items-center gap-2 text-sm font-medium text-foreground-900">
              <i className="ri-lightbulb-flash-line text-accent-600" aria-hidden="true"></i>
              本周看点
            </h2>
            <p className="mt-3 text-xs leading-relaxed text-foreground-600">{config.description}</p>
            <div className="mt-4 space-y-3">
              {sortTitles(filterTitles({ type: config.type }), "rating")
                .slice(0, 3)
                .map((item, index) => (
                  <Link
                    key={item.id}
                    to={`/detail/${item.id}`}
                    className="flex items-center gap-3 rounded-md bg-background-50 p-2 transition-colors cursor-pointer hover:bg-background-200/70"
                  >
                    <span className="font-heading text-lg font-bold text-background-400">
                      0{index + 1}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-medium text-foreground-900">{item.title}</p>
                      <p className="mt-0.5 text-[11px] text-foreground-500">
                        评分 {item.rating.toFixed(1)} · {item.year}
                      </p>
                    </div>
                    <i className="ri-arrow-right-up-line text-foreground-400" aria-hidden="true"></i>
                  </Link>
                ))}
            </div>
          </div>
        </div>

        <div className="mt-8 flex items-center justify-between gap-3">
          <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
            {genre === "全部" ? `全部${config.name}` : `${genre}类${config.name}`}
          </h2>
          <span className="text-xs text-foreground-500">已展示 {visible.length} / {list.length}</span>
        </div>

        {loading ? (
          <div className="mt-8 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5" aria-live="polite">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
                <div className="aspect-[3/4] w-full animate-pulse bg-background-200"></div>
                <div className="space-y-2 p-3">
                  <div className="h-3 w-2/3 animate-pulse rounded bg-background-200"></div>
                  <div className="h-3 w-1/3 animate-pulse rounded bg-background-100"></div>
                </div>
              </div>
            ))}
          </div>
        ) : list.length === 0 ? (
          <div className="mt-8 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-100 text-foreground-500">
              <i className="ri-inbox-line text-xl" aria-hidden="true"></i>
            </span>
            <h3 className="mt-4 text-sm font-medium text-foreground-900">暂无符合条件的影视作品</h3>
            <p className="mt-2 text-xs text-foreground-500">试试切换其他子分类，或重置当前筛选条件</p>
            <button
              type="button"
              onClick={() => setSearchParams({})}
              className="mt-5 rounded-md bg-primary-800 px-5 py-2 text-sm text-background-50 whitespace-nowrap transition-colors cursor-pointer hover:bg-primary-700"
            >
              重置筛选
            </button>
          </div>
        ) : (
          <>
            <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-5">
              {visible.map((item) => (
                <TitleCard key={item.id} item={item} />
              ))}
            </div>

            <div className="mt-8 flex flex-col items-center gap-4">
              {page < totalPages && (
                <button
                  type="button"
                  onClick={() => setPage((prev) => prev + 1)}
                  className="inline-flex items-center gap-2 rounded-md bg-secondary-500 px-6 py-2.5 text-sm font-medium text-primary-950 whitespace-nowrap transition-colors cursor-pointer hover:bg-secondary-400"
                >
                  <i className="ri-add-line" aria-hidden="true"></i>
                  加载更多（剩余 {list.length - visible.length} 部）
                </button>
              )}
              <div className="flex flex-wrap items-center justify-center gap-1.5">
                <button
                  type="button"
                  onClick={() => setPage(1)}
                  disabled={page === 1}
                  aria-label="第一页"
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 hover:border-primary-300"
                >
                  <i className="ri-skip-left-line text-sm" aria-hidden="true"></i>
                </button>
                {Array.from({ length: totalPages }).map((_, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => setPage(index + 1)}
                    aria-current={page === index + 1 ? "page" : undefined}
                    className={`h-8 min-w-8 rounded-md px-2 text-xs transition-colors cursor-pointer ${
                      page === index + 1
                        ? "bg-primary-800 text-background-50"
                        : "border border-background-300 text-foreground-700 hover:border-primary-300"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  type="button"
                  onClick={() => setPage(totalPages)}
                  disabled={page === totalPages}
                  aria-label="最后一页"
                  className="flex h-8 w-8 items-center justify-center rounded-md border border-background-300 text-foreground-600 transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-40 hover:border-primary-300"
                >
                  <i className="ri-skip-right-line text-sm" aria-hidden="true"></i>
                </button>
              </div>
              <p className="text-[11px] text-foreground-400">演示数据，分页仅作前端展示</p>
            </div>
          </>
        )}
      </div>
    </section>
  );
}